import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser, removeToken, isAdmin, isSecurityManager } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import {
  Home,
  Clock,
  FileText,
  Image,
  BarChart3,
  Users,
  LogOut,
  Menu,
  X,
  Building2,
  UserCog,
  Settings,
  ClipboardList,
  Briefcase,
  KeyRound,
} from 'lucide-react';

function AuthImage({ path, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    const loadImage = async () => {
      setFailed(false);
      setSrc(null);
      try {
        const cleanPath = path.replace(/^\/api/, '');
        const response = await api.get(cleanPath, { responseType: 'blob' });
        objectUrl = URL.createObjectURL(response.data);
        if (!cancelled) setSrc(objectUrl);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Gagal memuat logo:', error);
        }
        if (!cancelled) setFailed(true);
      }
    };

    if (path) loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (failed || !src) return null;

  return <img src={src} alt={alt} className={className} />;
}

function getMenuItems() {
  if (isSecurityManager()) {
    return [
      { path: '/', label: 'Dashboard', icon: Home },
      { path: '/teams', label: 'Teams Management', icon: Building2 },
      { path: '/change-password', label: 'Ganti Password', icon: KeyRound },
    ];
  }

  const items = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/timesheet', label: 'Timesheet', icon: FileText },
    { path: '/screenshots', label: 'Screenshots', icon: Image },
    { path: '/tasks', label: 'Tasks', icon: ClipboardList },
    { path: '/leave-requests', label: 'Leave Requests', icon: FileText },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
  ];

  items.push({ path: '/change-password', label: 'Ganti Password', icon: KeyRound });

  if (isAdmin()) {
    items.push({ path: '/employees-management', label: 'Manage Employees', icon: UserCog });
    items.push({ path: '/settings', label: 'Settings', icon: Settings });
  }

  return items;
}

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const menuItems = getMenuItems();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC]">
      {/* DESKTOP SIDEBAR - DEEP FOREST GREEN THEME */}
      <aside
        className="hidden lg:flex lg:flex-col lg:w-64 text-white border-r border-[#15342D] shadow-xl z-20"
        style={{
          background: 'linear-gradient(180deg, #1A3C34 0%, #122C26 50%, #0E221D 100%)',
        }}
      >
        {/* Brand & User Profile Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-2">
            {user?.team_logo_url && (
              <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/20 shadow-sm shrink-0 bg-white/10">
                <AuthImage
                  path={user.team_logo_url}
                  alt="Team Logo"
                  className="w-10 h-10 object-cover"
                />
              </div>
            )}
            <h1 className="text-2xl font-bold font-heading tracking-tight text-white" data-testid="app-logo">
              pantaukerja<span className="text-[#F97316]">.id</span>
            </h1>
          </div>
          <p className="text-sm font-semibold text-white/95 mt-2 truncate">{user?.name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/15 text-emerald-300 font-medium capitalize border border-white/10">
              {user?.role?.replace('_', ' ')}
            </span>
            {user?.team_name && (
              <span className="text-[11px] text-white/60 truncate" data-testid="sidebar-team-name">
                {user.team_name}
              </span>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-3.5 space-y-1.5 overflow-y-auto" data-testid="desktop-nav">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-medium text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-[#1A3C34] text-white font-semibold shadow-sm border border-white/15'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {React.createElement(item.icon, {
                  className: `w-4 h-4 ${isActive ? 'text-emerald-300' : 'text-white/60'}`,
                })}
                <span>{item.label}</span>
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Section */}
        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-rose-300 hover:bg-rose-500/20 hover:text-white transition-colors"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2.5 text-rose-400" />
            Logout
          </Button>
        </div>
      </aside>

      {/* MOBILE DRAWER */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside
            className="fixed left-0 top-0 bottom-0 w-64 text-white border-r border-[#15342D] z-50 flex flex-col shadow-2xl"
            style={{
              background: 'linear-gradient(180deg, #1A3C34 0%, #122C26 50%, #0E221D 100%)',
            }}
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2.5 mb-1">
                  {user?.team_logo_url && (
                    <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/20 shadow-sm shrink-0 bg-white/10">
                      <AuthImage
                        path={user.team_logo_url}
                        alt="Team Logo"
                        className="w-8 h-8 object-cover"
                      />
                    </div>
                  )}
                  <h1 className="text-2xl font-bold font-heading tracking-tight text-white">
                    pantaukerja<span className="text-[#F97316]">.id</span>
                  </h1>
                </div>
                <p className="text-sm font-semibold text-white/90 mt-1 truncate">{user?.name}</p>
                <p className="text-xs text-emerald-300 capitalize">{user?.role?.replace('_', ' ')}</p>
                {user?.team_name && (
                  <p className="text-xs text-white/60 mt-0.5">{user.team_name}</p>
                )}
              </div>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded-lg hover:bg-white/10 text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="flex-1 p-3.5 space-y-1.5 overflow-y-auto">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      isActive
                        ? 'bg-[#1A3C34] text-white font-semibold shadow-sm border border-white/15'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {React.createElement(item.icon, {
                      className: `w-4 h-4 ${isActive ? 'text-emerald-300' : 'text-white/60'}`,
                    })}
                    <span>{item.label}</span>
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 border-t border-white/10">
              <Button
                variant="ghost"
                className="w-full justify-start text-rose-300 hover:bg-rose-500/20 hover:text-white transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2.5 text-rose-400" />
                Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header with Deep Forest Green */}
        <header className="lg:hidden bg-[#1A3C34] text-white border-b border-[#142E27] p-4 flex items-center justify-between shadow-sm">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button" className="p-1 rounded-lg hover:bg-white/10">
            <Menu className="w-6 h-6 text-white" />
          </button>
          <h1 className="text-xl font-bold font-heading tracking-tight">
            pantaukerja<span className="text-[#F97316]">.id</span>
          </h1>
          <div className="w-6" />
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-10 bg-[#F8FAFC]">
          {children}
        </main>
      </div>
    </div>
  );
}